/* eslint-disable */

import api from './api';
import MockApi from './apiMock';
import handleErrors from './handleErrors';
import { sendEvent } from '../utils/eventUtils';
import { TANGO_CLIENT_ID } from '../constants';
import { getFreshAuth0AccessToken, isAuth0AccessTokenExpired } from '../auth/auth0TokenBridge';

const AUTH_TOKEN_STORAGE_KEY = 'authToken';
const MAX_RETRY_ATTEMPTS = 4;
const INITIAL_BACKOFF_MS = 250;
const inFlightRequests = new Map();

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const getResponseStatus = error => (error && error.response && error.response.status) || null;

const isClientError = status => status >= 400 && status < 500;

const isRetryableError = error => {
  const status = getResponseStatus(error);

  if (status && isClientError(status)) {
    return false;
  }

  if (status && status >= 500) {
    return true;
  }

  return !status;
};

const executeRequest = async (config, options, authToken, fetch, state) => {
  const promise = authToken === '1234'
    ? MockApi.handle(options.api, config)
    : api(options, config, authToken);

  const response = await promise(fetch.url, fetch.options);
  const handledResponse = await handleErrors(response, state.clientId, state.userId);

  return handleJson(handledResponse);
};

const getRequestKey = ({ fetch }) => {
  const requestOptions = fetch.options || {};
  return JSON.stringify({
    api: fetch.api || 'carma',
    unsecured: !!fetch.unsecured,
    url: fetch.url,
    method: requestOptions.method || 'get',
    body: requestOptions.body || null,
  });
};

function isRequest({ types, fetch }) {
  return Array.isArray(types) && types.length === 3 &&
    fetch && fetch.url;
}

const handleJson = response => {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  return isJson ? response.json() : response;
};

const handleEvent = (data, { fetch, REQUEST }) => {
  if (REQUEST.startsWith('SUBMIT_')) {
    const event = {
      version: '1.0',
      eventType: 'API_REQUEST',
      status: 'SUCCESS',
      context: REQUEST,
      url: fetch.url,
    };

    sendEvent(event);
  }

  return data;
};

const handleEventError = (error, { fetch, REQUEST }) => {
  if (REQUEST.startsWith('SUBMIT_')) {
    const event = {
      version: '1.0',
      eventType: 'API_REQUEST',
      status: 'ERROR',
      context: REQUEST,
      url: fetch.url,
    };
    sendEvent(event);
  }

  return error;
};

async function makeRequest(store, { fetch, types, ...rest }, next) {
  const [REQUEST, SUCCESS, ERROR] = types;
  const requestKey = getRequestKey({ fetch });

  if (inFlightRequests.has(requestKey)) {
    return inFlightRequests.get(requestKey);
  }

  const requestPromise = (async () => {
    next({ ...rest, type: REQUEST, fetch, isRequest: true });

    const state = store.getState();
    const config = state.configuration;
    let authToken = state.authentication.authToken;
    const shouldUseInAppTokenRotation = state.clientId === TANGO_CLIENT_ID;
    const options = { api: fetch.api || 'carma', secured: !fetch.unsecured };

    if (shouldUseInAppTokenRotation && options.secured && authToken !== '1234') {
      const refreshedToken = await getFreshAuth0AccessToken();
      if (refreshedToken && !isAuth0AccessTokenExpired(refreshedToken)) {
        authToken = refreshedToken;
        sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, refreshedToken);
      } else {
        authToken = null;
      }
    }

    if (
      shouldUseInAppTokenRotation &&
      options.secured &&
      authToken !== '1234' &&
      (!authToken || isAuth0AccessTokenExpired(authToken))
    ) {
      const error = new Error('Unable to refresh access token');
      error.response = {
        status: 401,
        statusText: 'Unauthorized',
      };

      const finalAction = {
        ...rest,
        error: handleEventError(error, { fetch, REQUEST }),
        type: ERROR,
        fetch,
        isError: true,
      };

      next(finalAction);
      return finalAction;
    }

    let attempt = 0;
    let lastError = null;

    while (attempt < MAX_RETRY_ATTEMPTS) {
      try {
        const data = await executeRequest(config, options, authToken, fetch, state);
        const action = {
          ...rest,
          payload: handleEvent(data, { fetch, REQUEST }),
          type: SUCCESS,
          fetch,
          isSuccess: true,
        };

        next(action);
        return action;
      } catch (error) {
        lastError = error;

        if (!isRetryableError(error) || attempt === MAX_RETRY_ATTEMPTS - 1) {
          const finalAction = {
            ...rest,
            error: handleEventError(error, { fetch, REQUEST }),
            type: ERROR,
            fetch,
            // do not show <ErrorBar /> due to Plaid logs issues
            isError: !(fetch.api === 'carma' && fetch.url.endsWith('plaid/log')),
          };

          next(finalAction);
          return finalAction;
        }

        attempt += 1;
        const backoffMs = INITIAL_BACKOFF_MS * (2 ** (attempt - 1));
        await delay(backoffMs);
      }
    }

    const finalAction = {
      ...rest,
      error: handleEventError(lastError, { fetch, REQUEST }),
      type: ERROR,
      fetch,
      isError: !(fetch.api === 'carma' && fetch.url.endsWith('plaid/log')),
    };

    next(finalAction);
    return finalAction;
  })();

  inFlightRequests.set(requestKey, requestPromise);

  return requestPromise.finally(() => {
    inFlightRequests.delete(requestKey);
  });
}

const requestThunk = store => next => action =>
  (isRequest(action) ?
    makeRequest(store, action, next) :
    next(action));

export default requestThunk;
